import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { LimitPriceTokenSide } from '@/components/TransactionModule/LimitMode/LimitPriceShortcuts';
import {
  getSellAndBuyRates,
  makePairKey,
} from '@/components/TransactionModule/LimitMode/useLimitOrderRate';
import {
  computeLimitPriceBlock,
  computeTriggerPriceBlock,
} from '@/components/TransactionModule/LimitMode/utils';

import { isTriggerOrderType, type AdvancedOrderType } from '@/constants/advancedOrder';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { isUsdStableAddress } from '@/utils/multichain';

import { useLimitRateStore } from './limitRateStore';
import type { TokenUsdPrices } from './tradeQuote';

type UseLimitRateParams = {
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
  isLoading: boolean;
  /** Engine-owned USD prices; the hook derives the market rate instead of refetching. */
  tokenUsdPrices: TokenUsdPrices;
  /** True once the engine's USD price query answered, even with no prices. */
  hasUsdPriceResponse: boolean;
  /** Whether the market rate should seed an empty rate for the current pair. */
  autoInit?: boolean;
  /** Nested Advanced order type used to apply Limit or trigger blocking semantics. */
  orderType?: AdvancedOrderType;
};

/** Controls the pair-tagged limit rate using rewrite-owned state. */
export function useLimitRate({
  sellToken,
  buyToken,
  isLoading,
  tokenUsdPrices,
  hasUsdPriceResponse,
  autoInit = true,
  orderType = 'limit',
}: UseLimitRateParams) {
  const taggedRate = useLimitRateStore((state) => state.taggedLimitRate);
  const limitPriceSideOverride = useLimitRateStore((state) => state.limitPriceSideOverride);
  const setTaggedRate = useLimitRateStore((state) => state.setTaggedLimitRate);
  const setLimitPriceSideOverride = useLimitRateStore((state) => state.setLimitPriceSideOverride);
  const resetStore = useLimitRateStore((state) => state.reset);
  const previousOrderTypeRef = useRef(orderType);

  const sellTokenInfo = useMemo(
    () => (sellToken ? { address: sellToken.address, chainId: sellToken.chainId } : undefined),
    [sellToken],
  );
  const buyTokenInfo = useMemo(
    () => (buyToken ? { address: buyToken.address, chainId: buyToken.chainId } : undefined),
    [buyToken],
  );
  const currentPairKey = useMemo(
    () => makePairKey(sellTokenInfo, buyTokenInfo),
    [sellTokenInfo, buyTokenInfo],
  );
  const swappedPairKey = useMemo(
    () => makePairKey(buyTokenInfo, sellTokenInfo),
    [buyTokenInfo, sellTokenInfo],
  );

  const defaultSide = useMemo<LimitPriceTokenSide>(() => {
    const sellIsStable = sellTokenInfo
      ? isUsdStableAddress(sellTokenInfo.address, sellTokenInfo.chainId)
      : false;
    const buyIsStable = buyTokenInfo
      ? isUsdStableAddress(buyTokenInfo.address, buyTokenInfo.chainId)
      : false;
    if (sellIsStable && !buyIsStable) return 'buy';
    return 'sell';
  }, [buyTokenInfo, sellTokenInfo]);

  const side =
    limitPriceSideOverride?.pairKey === currentPairKey ? limitPriceSideOverride.side : defaultSide;
  const isRateForCurrentPair = Boolean(currentPairKey && taggedRate.pairKey === currentPairKey);
  const rate = useMemo(
    () => (isRateForCurrentPair ? taggedRate.rate : { sell: '', buy: '' }),
    [isRateForCurrentPair, taggedRate.rate],
  );

  const marketRate =
    hasUsdPriceResponse && tokenUsdPrices.sellTokenUsd && tokenUsdPrices.buyTokenUsd
      ? String(tokenUsdPrices.sellTokenUsd / tokenUsdPrices.buyTokenUsd)
      : '';

  useEffect(() => {
    if (previousOrderTypeRef.current === orderType) return;
    previousOrderTypeRef.current = orderType;
    resetStore();
  }, [orderType, resetStore]);

  useEffect(() => {
    if (!autoInit) return;
    const hasPendingSwappedRate =
      taggedRate.pairKey === swappedPairKey &&
      (taggedRate.rate.sell !== '' || taggedRate.rate.buy !== '');
    if (
      sellToken &&
      buyToken &&
      rate.sell === '' &&
      !hasPendingSwappedRate &&
      marketRate &&
      !isLoading
    ) {
      const { sellRate, buyRate } = getSellAndBuyRates(marketRate, 'sell');
      setTaggedRate({ sell: sellRate, buy: buyRate }, currentPairKey);
    }
  }, [
    autoInit,
    buyToken,
    currentPairKey,
    isLoading,
    marketRate,
    rate.sell,
    sellToken,
    setTaggedRate,
    swappedPairKey,
    taggedRate.pairKey,
    taggedRate.rate.buy,
    taggedRate.rate.sell,
  ]);

  const setRate = useCallback(
    (newRate: string, newSide: LimitPriceTokenSide) => {
      const { sellRate, buyRate } = getSellAndBuyRates(newRate, newSide);
      setTaggedRate({ sell: sellRate, buy: buyRate }, currentPairKey);
    },
    [currentPairKey, setTaggedRate],
  );

  const flipSide = useCallback(() => {
    const next: LimitPriceTokenSide = side === 'sell' ? 'buy' : 'sell';
    setLimitPriceSideOverride({ side: next, pairKey: currentPairKey });
  }, [currentPairKey, setLimitPriceSideOverride, side]);

  const flipTokens = useCallback(() => {
    const previousRate = useLimitRateStore.getState().taggedLimitRate.rate;
    setTaggedRate({ sell: previousRate.buy, buy: previousRate.sell }, swappedPairKey);
  }, [setTaggedRate, swappedPairKey]);

  const limitBlockResult = useMemo(() => {
    const parsedMarketRate = Number(marketRate);

    if (
      Number.isNaN(parsedMarketRate) ||
      parsedMarketRate === 0 ||
      rate.sell === '' ||
      Number.isNaN(Number(rate.sell))
    ) {
      return { blocked: false, differencePercent: 0 };
    }

    if (isTriggerOrderType(orderType)) {
      const triggerBlock = computeTriggerPriceBlock(orderType, Number(rate.sell), parsedMarketRate);
      const displayedTrigger = Number(rate[side]);
      const displayedMarket = side === 'sell' ? parsedMarketRate : 1 / parsedMarketRate;
      const differencePercent =
        Number.isNaN(displayedTrigger) || displayedMarket === 0
          ? triggerBlock.differencePercent
          : (displayedTrigger - displayedMarket) / displayedMarket;
      return { blocked: triggerBlock.blocked, differencePercent };
    }

    const currentLimitRate = rate[side];
    const parsedLimitRate = Number(currentLimitRate);
    if (Number.isNaN(parsedLimitRate) || currentLimitRate === '') {
      return { blocked: false, differencePercent: 0 };
    }
    const processedMarketRate = side === 'sell' ? parsedMarketRate : 1 / parsedMarketRate;
    return computeLimitPriceBlock(side, parsedLimitRate, processedMarketRate);
  }, [marketRate, orderType, rate, side]);

  return {
    rate,
    side,
    flipSide,
    flipTokens,
    setRate,
    reset: resetStore,
    marketRate,
    limitBlockResult,
  };
}
